import { useEffect, useId, useRef, type CSSProperties, type ReactNode } from 'react';
import { RotateCcw, Trophy } from 'lucide-react';

export interface ArcadeSummaryStat {
  label: string;
  value: ReactNode;
}

interface ArcadeSummaryViewProps {
  heading: string;
  headingLevel?: 'h1' | 'h2';
  verifiedScore: number;
  accuracy: number;
  correctCount: number;
  answeredCount: number;
  isNewPersonalBest: boolean;
  additionalStats?: readonly ArcadeSummaryStat[];
  playAgainLabel?: string;
  playAgainDisabled?: boolean;
  playAgainTestId?: string;
  onPlayAgain: () => void;
  children?: ReactNode;
  className?: string;
  testId?: string;
}

export function ArcadeSummaryView({
  heading,
  headingLevel = 'h2',
  verifiedScore,
  accuracy,
  correctCount,
  answeredCount,
  isNewPersonalBest,
  additionalStats = [],
  playAgainLabel = 'Play again',
  playAgainDisabled = false,
  playAgainTestId,
  onPlayAgain,
  children,
  className = '',
  testId,
}: ArcadeSummaryViewProps) {
  const Heading = headingLevel;
  const headingId = useId();
  const summaryRef = useRef<HTMLElement>(null);

  useEffect(() => {
    summaryRef.current?.focus();
  }, []);

  return (
    <section
      ref={summaryRef}
      className={`arcade-summary-view ${className}`.trim()}
      aria-labelledby={headingId}
      role="status"
      tabIndex={-1}
      data-testid={testId}
    >
      <div className="arcade-summary-heading">
        <Trophy aria-hidden="true" />
        <Heading id={headingId}>{heading}</Heading>
        {isNewPersonalBest && <p className="arcade-summary-personal-best">★ New personal best</p>}
      </div>

      <dl
        className="arcade-summary-stats"
        style={
          {
            '--arcade-summary-stat-count': 3 + additionalStats.length,
          } as CSSProperties
        }
      >
        <div className="arcade-summary-stat">
          <dt>Score</dt>
          <dd>{verifiedScore.toLocaleString()} points</dd>
        </div>
        <div className="arcade-summary-stat">
          <dt>Accuracy</dt>
          <dd>{Math.round(accuracy * 100)}%</dd>
        </div>
        <div className="arcade-summary-stat">
          <dt>Correct</dt>
          <dd>
            {correctCount}/{answeredCount}
          </dd>
        </div>
        {additionalStats.map(stat => (
          <div className="arcade-summary-stat" key={stat.label}>
            <dt>{stat.label}</dt>
            <dd>{stat.value}</dd>
          </div>
        ))}
      </dl>

      {children && <div className="arcade-summary-details">{children}</div>}

      <button
        className="arcade-summary-action"
        type="button"
        onClick={onPlayAgain}
        disabled={playAgainDisabled}
        data-testid={playAgainTestId}
      >
        <RotateCcw aria-hidden="true" />
        {playAgainLabel}
      </button>
    </section>
  );
}
